import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useFundingFunnel } from '../operations/funnel-react.js';
import { connectionFailureCode } from './connection-error.js';
import { fundingProfilePresentation, fundingProfileQuoteIntent, readFundingProfile } from './funding-profile.js';
export function useFundingProfile(options) {
  const $ = _c(55);
  const {
    client,
    provider
  } = options;
  const cache = useQueryClient();
  const t0 = options.connection.canConnect ?? false;
  const t1 = options.connection.verification?.customerId ?? null;
  const t2 = options.connection.quoteContext?.context.destination ?? null;
  const t3 = options.connection.quoteContext?.context.asset ?? null;
  let t4;
  if ($[0] !== options.connection.quoteContext?.methods) {
    t4 = options.connection.quoteContext?.methods ?? [];
    $[0] = options.connection.quoteContext?.methods;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== client.cacheKey || $[3] !== options.connection.guest || $[4] !== options.currency || $[5] !== options.decimals || $[6] !== options.sessionProofId || $[7] !== t0 || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = ["onramp", client.cacheKey, "profile", options.sessionProofId, options.currency, options.decimals, options.connection.guest, t0, t1, t2, t3, t4];
    $[2] = client.cacheKey;
    $[3] = options.connection.guest;
    $[4] = options.currency;
    $[5] = options.decimals;
    $[6] = options.sessionProofId;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const queryKey = t5;
  let t6;
  if ($[13] !== cache || $[14] !== client || $[15] !== options.connection || $[16] !== options.currency || $[17] !== options.decimals || $[18] !== options.sessionProofId || $[19] !== provider) {
    t6 = t7 => {
      const {
        signal
      } = t7;
      return readFundingProfile(client, provider, signal, options.connection, fundingProfileQuoteIntent(options.connection, options.currency, options.decimals, profileQuoteIntent(cache.getQueryData(["onramp", client.cacheKey, "profile-quote", options.sessionProofId]), options.currency, options.decimals)));
    };
    $[13] = cache;
    $[14] = client;
    $[15] = options.connection;
    $[16] = options.currency;
    $[17] = options.decimals;
    $[18] = options.sessionProofId;
    $[19] = provider;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      persist: false
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== queryKey || $[23] !== t6) {
    t8 = {
      queryKey,
      queryFn: t6,
      retry: false,
      staleTime: 0,
      gcTime: 0,
      meta: t7,
      refetchOnWindowFocus: false
    };
    $[22] = queryKey;
    $[23] = t6;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const query = useQuery(t8);
  let t9;
  if ($[25] !== cache || $[26] !== client.cacheKey || $[27] !== options.sessionProofId || $[28] !== query.data?.verification) {
    t9 = () => {
      const verification = query.data?.verification;
      if (!verification) {
        return;
      }
      cache.setQueriesData({
        queryKey: ["onramp", client.cacheKey, "discovery", options.sessionProofId]
      }, previous => previous?.customerVerification?.customerId === verification.customerId ? {
        ...previous,
        customerVerification: verification
      } : previous);
    };
    $[25] = cache;
    $[26] = client.cacheKey;
    $[27] = options.sessionProofId;
    $[28] = query.data?.verification;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = query.data?.verification;
  let t11;
  if ($[30] !== cache || $[31] !== client || $[32] !== options.sessionProofId || $[33] !== t10) {
    t11 = [cache, client, options.sessionProofId, t10];
    $[30] = cache;
    $[31] = client;
    $[32] = options.sessionProofId;
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  useEffect(t9, t11);
  let t12;
  let t13;
  if ($[35] !== cache || $[36] !== client) {
    t12 = () => {
      const stop = () => {
        cache.removeQueries({
          queryKey: ["onramp", client.cacheKey, "profile"]
        });
      };
      const unregister = client.onDispose(stop);
      return () => {
        unregister();
        stop();
      };
    };
    t13 = [client, cache];
    $[35] = cache;
    $[36] = client;
    $[37] = t12;
    $[38] = t13;
  } else {
    t12 = $[37];
    t13 = $[38];
  }
  useEffect(t12, t13);
  let t14;
  if ($[39] !== query.data?.limitsUnavailable || $[40] !== query.error || $[41] !== query.isError) {
    t14 = connectionFailureCode(query.error) ?? (query.isError || query.data?.limitsUnavailable ? "onramp_refresh_failed" : null);
    $[39] = query.data?.limitsUnavailable;
    $[40] = query.error;
    $[41] = query.isError;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const errorCode = t14;
  const t15 = query.isFetching ? "loading" : query.isError || query.data?.limitsUnavailable ? "error" : "ready";
  let t16;
  if ($[43] !== errorCode || $[44] !== t15) {
    t16 = {
      stage: "profile",
      outcome: t15,
      errorCode
    };
    $[43] = errorCode;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  useFundingFunnel(client, t16);
  const t17 = query.isPending;
  const t18 = query.isError;
  const t19 = query.data;
  let t20;
  if ($[46] !== query.data?.limits) {
    t20 = query.data?.limits ?? [];
    $[46] = query.data?.limits;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== query.data || $[49] !== t20) {
    t21 = fundingProfilePresentation(query.data, t20);
    $[48] = query.data;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== errorCode || $[52] !== query || $[53] !== t21) {
    t22 = {
      loading: t17,
      error: t18,
      errorCode,
      data: t19,
      presentation: t21,
      refresh() {
        query.refetch();
      }
    };
    $[51] = errorCode;
    $[52] = query;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}
function profileQuoteIntent(memory, currency, decimals) {
  return memory && Date.now() - memory.recordedAt < 30 * 60 * 1000 && memory.intent.fiatCurrency === currency && memory.intent.fiatDecimals === decimals ? memory.intent : undefined;
}
/** Remember only a previously quoted intent; the profile obtains a NEW quote for current limits. */
export function useFundingProfileQuote(client, sessionProofId, intent, review) {
  const $ = _c(11);
  const cache = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== cache || $[1] !== client || $[2] !== intent || $[3] !== review || $[4] !== sessionProofId) {
    t0 = () => {
      if (!intent || !review || review.state === "indicative" || Date.parse(review.expiresAt) <= Date.now()) {
        return;
      }
      const key = ["onramp", client.cacheKey, "profile-quote", sessionProofId];
      cache.setQueryDefaults(key, {
        gcTime: 1800000,
        meta: {
          persist: false
        }
      });
      cache.setQueryData(key, {
        intent,
        recordedAt: Date.now()
      });
    };
    t1 = [client, cache, intent, review, sessionProofId];
    $[0] = cache;
    $[1] = client;
    $[2] = intent;
    $[3] = review;
    $[4] = sessionProofId;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[7] !== cache || $[8] !== client) {
    t2 = () => retainProfileQuoteCleanup(client, cache);
    t3 = [client, cache];
    $[7] = cache;
    $[8] = client;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
}
const quoteOwners = new WeakMap();
function retainProfileQuoteCleanup(client, cache) {
  let caches = quoteOwners.get(client);
  if (!caches) {
    caches = new WeakSet();
    quoteOwners.set(client, caches);
  }
  if (caches.has(cache)) return;
  caches.add(cache);
  // A quote survives the checkout unmount; its cleanup must survive it too.
  client.onDispose(() => {
    cache.removeQueries({
      queryKey: ['onramp', client.cacheKey, 'profile-quote']
    });
  });
}